import React, { useState, useEffect } from 'react';
import { AlarmasLayout } from '../../components/alarmas/AlarmasLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Slider } from '../../components/ui/slider';
import { toast } from 'sonner';
import { Plus, Trash2, Shield, Users, Wifi, RefreshCw, Power, UserPlus, WifiOff, Volume2, MessageCircle, KeyRound, Link2, Copy, Check, X as XIcon, MapPin, Clock, Hand, ShieldAlert, Stethoscope, HelpCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SOUND_OPTIONS = [
    { value: 'clasico', label: 'Clasico' },
    { value: 'urgente', label: 'Urgente' },
    { value: 'sirena', label: 'Sirena' },
    { value: 'suave', label: 'Suave' },
    { value: 'silencio', label: 'Silencioso' },
];

export default function AlarmasAdminPage() {
    const [tuyaDevices, setTuyaDevices] = useState([]);
    const [registrados, setRegistrados] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [asignaciones, setAsignaciones] = useState([]);
    const [preferencias, setPreferencias] = useState({});
    const [chatConfig, setChatConfig] = useState({ activo: false, duracion_minutos: 15 });
    const [loading, setLoading] = useState(true);
    const [tuyaError, setTuyaError] = useState('');

    // Nuevos estados
    const [longPressMs, setLongPressMs] = useState(800);
    const [sosLongPressMs, setSosLongPressMs] = useState(1200);
    const [registroLinks, setRegistroLinks] = useState([]);
    const [pendientes, setPendientes] = useState([]);
    const [newLinkDays, setNewLinkDays] = useState(7);
    const [copiedLinkId, setCopiedLinkId] = useState('');
    const [dialogEditDireccion, setDialogEditDireccion] = useState({ open: false, userId: '', userName: '', nombre: '', direccion: '', email: '' });
    const [diagnosticos, setDiagnosticos] = useState([]);

    // Dialogs
    const [dialogAdd, setDialogAdd] = useState({ open: false, device: null });
    const [dialogAddManual, setDialogAddManual] = useState(false);
    const [dialogUser, setDialogUser] = useState(false);
    const [dialogPwd, setDialogPwd] = useState({ open: false, userId: '', userName: '' });
    const [adminNewPwd, setAdminNewPwd] = useState('');
    const [formNombre, setFormNombre] = useState('');
    const [formDeviceId, setFormDeviceId] = useState('');
    const [formSwitch, setFormSwitch] = useState('switch_1');
    const [formUser, setFormUser] = useState({ nombre: '', direccion: '', email: '', password: '' });

    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    useEffect(() => { loadAll(); }, []);

    const loadAll = async () => {
        setLoading(true);
        setTuyaError('');
        try {
            const [rRes, uRes, aRes] = await Promise.all([
                fetch(`${API_URL}/api/alarmas/dispositivos`, { headers }),
                fetch(`${API_URL}/api/usuarios`, { headers }),
                fetch(`${API_URL}/api/alarmas/asignaciones`, { headers }),
            ]);
            if (rRes.ok) setRegistrados(await rRes.json());
            if (uRes.ok) {
                const usrs = await uRes.json();
                setUsuarios(usrs);
                // Cargar preferencias de sonido de cada usuario
                const prefsMap = {};
                for (const u of usrs) {
                    try {
                        // Use admin endpoint would need a list endpoint, use individual
                        prefsMap[u.id] = 'clasico';
                    } catch { /* ignore */ }
                }
                setPreferencias(prefsMap);
            }
            if (aRes.ok) setAsignaciones(await aRes.json());
            // Chat config
            try {
                const ccRes = await fetch(`${API_URL}/api/alarmas/chat/config`, { headers });
                if (ccRes.ok) setChatConfig(await ccRes.json());
            } catch { /* ignore */ }
            // Public config (long-press)
            try {
                const pcRes = await fetch(`${API_URL}/api/alarmas/public-config`);
                if (pcRes.ok) {
                    const d = await pcRes.json();
                    setLongPressMs(d.long_press_ms || 800);
                    setSosLongPressMs(d.sos_long_press_ms || 1200);
                }
            } catch { /* ignore */ }
            // Links de registro
            try {
                const lRes = await fetch(`${API_URL}/api/alarmas/admin/registro-links`, { headers });
                if (lRes.ok) setRegistroLinks(await lRes.json());
            } catch { /* ignore */ }
            // Pendientes
            try {
                const pRes = await fetch(`${API_URL}/api/alarmas/admin/usuarios-pendientes`, { headers });
                if (pRes.ok) setPendientes(await pRes.json());
            } catch { /* ignore */ }
            // Diagnósticos
            try {
                const dRes = await fetch(`${API_URL}/api/alarmas/admin/diagnosticos`, { headers });
                if (dRes.ok) setDiagnosticos(await dRes.json());
            } catch { /* ignore */ }
        } catch (e) {
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
        // Cargar Tuya por separado para no bloquear
        loadTuya();
    };

    const loadTuya = async () => {
        setTuyaError('');
        try {
            const tRes = await fetch(`${API_URL}/api/alarmas/tuya/dispositivos`, { headers });
            if (tRes.ok) {
                const data = await tRes.json();
                setTuyaDevices(Array.isArray(data) ? data : []);
            } else {
                const err = await tRes.json().catch(() => ({}));
                setTuyaError(err.detail || `Error ${tRes.status}`);
            }
        } catch {
            setTuyaError('Error de conexion');
        }
    };

    // --- Registrar dispositivo desde Tuya ---
    const handleRegistrar = async () => {
        if (!dialogAdd.device || !formNombre.trim()) return;
        try {
            const res = await fetch(`${API_URL}/api/alarmas/dispositivos`, {
                method: 'POST', headers,
                body: JSON.stringify({
                    tuya_device_id: dialogAdd.device.tuya_device_id,
                    nombre: formNombre.trim(),
                    codigo_switch: 'switch_1',
                }),
            });
            if (res.ok) {
                toast.success('Dispositivo registrado');
                setDialogAdd({ open: false, device: null });
                setFormNombre('');
                loadAll();
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Error');
            }
        } catch { toast.error('Error'); }
    };

    // --- Registrar dispositivo manualmente ---
    const handleRegistrarManual = async () => {
        if (!formNombre.trim() || !formDeviceId.trim()) {
            toast.error('Nombre y Device ID son obligatorios');
            return;
        }
        try {
            const res = await fetch(`${API_URL}/api/alarmas/dispositivos`, {
                method: 'POST', headers,
                body: JSON.stringify({
                    tuya_device_id: formDeviceId.trim(),
                    nombre: formNombre.trim(),
                    codigo_switch: formSwitch || 'switch_1',
                }),
            });
            if (res.ok) {
                toast.success('Dispositivo registrado');
                setDialogAddManual(false);
                setFormNombre(''); setFormDeviceId(''); setFormSwitch('switch_1');
                loadAll();
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Error');
            }
        } catch { toast.error('Error'); }
    };

    // --- Eliminar dispositivo ---
    const handleEliminar = async (id) => {
        if (!confirm('¿Eliminar dispositivo?')) return;
        const res = await fetch(`${API_URL}/api/alarmas/dispositivos/${id}`, { method: 'DELETE', headers });
        if (res.ok) { toast.success('Eliminado'); loadAll(); }
    };

    // --- Crear usuario ---
    const handleCrearUsuario = async () => {
        if (!formUser.nombre || !formUser.email || !formUser.password) {
            toast.error('Complete nombre, email y contraseña');
            return;
        }
        try {
            const res = await fetch(`${API_URL}/api/alarmas/usuarios`, {
                method: 'POST', headers,
                body: JSON.stringify(formUser),
            });
            if (res.ok) {
                toast.success('Usuario creado');
                setDialogUser(false);
                setFormUser({ nombre: '', direccion: '', email: '', password: '' });
                loadAll();
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Error al crear usuario');
            }
        } catch { toast.error('Error'); }
    };

    // --- Long-press config ---
    const handleSaveLongPress = async (ms) => {
        setLongPressMs(ms);
        try {
            await fetch(`${API_URL}/api/alarmas/admin/long-press`, {
                method: 'PUT', headers,
                body: JSON.stringify({ long_press_ms: ms }),
            });
        } catch { /* ignore */ }
    };

    const handleSaveSosLongPress = async (ms) => {
        setSosLongPressMs(ms);
        try {
            await fetch(`${API_URL}/api/alarmas/admin/long-press`, {
                method: 'PUT', headers,
                body: JSON.stringify({ sos_long_press_ms: ms }),
            });
        } catch { /* ignore */ }
    };

    // --- Registro links ---
    const handleCrearLink = async () => {
        try {
            const res = await fetch(`${API_URL}/api/alarmas/admin/registro-links`, {
                method: 'POST', headers,
                body: JSON.stringify({ valid_days: newLinkDays }),
            });
            if (res.ok) {
                toast.success('Link creado');
                loadAll();
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Error');
            }
        } catch { toast.error('Error'); }
    };

    const handleCopyLink = (link) => {
        // Apunta al endpoint backend que sirve HTML con meta OG dinámicos
        // (logo custom + nombre empresa) — así WhatsApp/redes muestran el preview correcto.
        // El endpoint redirige automáticamente al frontend cuando un humano lo abre.
        const url = `${window.location.origin}/api/alarmas/r/${link.token}`;
        navigator.clipboard.writeText(url).then(() => {
            setCopiedLinkId(link.id);
            toast.success('Link copiado al portapapeles');
            setTimeout(() => setCopiedLinkId(''), 2000);
        }).catch(() => {
            // Fallback
            const ta = document.createElement('textarea');
            ta.value = url;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
            toast.success('Link copiado');
        });
    };

    const handleRevocarLink = async (id) => {
        if (!confirm('¿Revocar este link? Dejará de funcionar.')) return;
        try {
            await fetch(`${API_URL}/api/alarmas/admin/registro-links/${id}`, { method: 'DELETE', headers });
            toast.success('Revocado');
            loadAll();
        } catch { toast.error('Error'); }
    };

    // --- Pendientes ---
    const handleAprobar = async (id) => {
        try {
            const res = await fetch(`${API_URL}/api/alarmas/admin/usuarios-pendientes/${id}/aprobar`, { method: 'POST', headers });
            if (res.ok) { toast.success('Usuario aprobado'); loadAll(); }
            else { const e = await res.json(); toast.error(e.detail || 'Error'); }
        } catch { toast.error('Error'); }
    };

    const handleRechazar = async (id) => {
        if (!confirm('¿Rechazar esta solicitud?')) return;
        try {
            await fetch(`${API_URL}/api/alarmas/admin/usuarios-pendientes/${id}/rechazar`, { method: 'POST', headers });
            toast.success('Rechazado');
            loadAll();
        } catch { toast.error('Error'); }
    };

    // --- Editar usuario completo (admin) ---
    const handleSaveDireccionAdmin = async () => {
        const { userId, nombre, direccion, email } = dialogEditDireccion;
        if (!nombre || nombre.trim().length < 2) {
            toast.error('Nombre inválido'); return;
        }
        try {
            const res = await fetch(`${API_URL}/api/alarmas/admin/usuarios/${userId}`, {
                method: 'PUT', headers,
                body: JSON.stringify({
                    nombre: nombre.trim(),
                    direccion: direccion.trim(),
                    email: email.trim().toLowerCase(),
                }),
            });
            if (res.ok) {
                toast.success('Usuario actualizado');
                setDialogEditDireccion({ open: false, userId: '', userName: '', nombre: '', direccion: '', email: '' });
                loadAll();
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Error');
            }
        } catch { toast.error('Error'); }
    };

    // --- Asignar dispositivo ---
    const handleAsignar = async (userId, dispId) => {
        try {
            const res = await fetch(`${API_URL}/api/alarmas/asignaciones`, {
                method: 'POST', headers,
                body: JSON.stringify({ usuario_id: userId, dispositivo_id: dispId }),
            });
            if (res.ok) { toast.success('Asignado'); loadAll(); }
            else { const e = await res.json(); toast.error(e.detail || 'Error'); }
        } catch { toast.error('Error'); }
    };

    // --- Desasignar ---
    const handleDesasignar = async (asigId) => {
        const res = await fetch(`${API_URL}/api/alarmas/asignaciones/${asigId}`, { method: 'DELETE', headers });
        if (res.ok) { toast.success('Desasignado'); loadAll(); }
    };

    const handleSetSound = async (userId, sound) => {
        setPreferencias(prev => ({ ...prev, [userId]: sound }));
        try {
            await fetch(`${API_URL}/api/alarmas/admin/preferencias/${userId}`, {
                method: 'PUT', headers,
                body: JSON.stringify({ sonido: sound }),
            });
            toast.success('Sonido actualizado');
        } catch { toast.error('Error'); }
    };

    const handleTogglePrueba = async (userId, current) => {
        try {
            await fetch(`${API_URL}/api/alarmas/admin/toggle-prueba/${userId}`, {
                method: 'PUT', headers,
                body: JSON.stringify({ puede_probar: !current }),
            });
            toast.success(!current ? 'Pruebas habilitadas' : 'Pruebas deshabilitadas');
            setPreferencias(prev => ({ ...prev, [`test_${userId}`]: !current }));
        } catch { toast.error('Error'); }
    };

    const handleSaveChatConfig = async (newConfig) => {
        setChatConfig(newConfig);
        try {
            await fetch(`${API_URL}/api/alarmas/chat/config`, {
                method: 'PUT', headers,
                body: JSON.stringify(newConfig),
            });
            toast.success('Configuracion de chat guardada');
        } catch { toast.error('Error'); }
    };

    const handleAdminChangePwd = async () => {
        if (!adminNewPwd || adminNewPwd.length < 6) {
            toast.error('Minimo 6 caracteres'); return;
        }
        try {
            const res = await fetch(`${API_URL}/api/alarmas/admin/cambiar-password/${dialogPwd.userId}`, {
                method: 'PUT', headers,
                body: JSON.stringify({ new_password: adminNewPwd }),
            });
            if (res.ok) {
                toast.success('Contrasena actualizada');
                setDialogPwd({ open: false, userId: '', userName: '' });
                setAdminNewPwd('');
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Error');
            }
        } catch { toast.error('Error'); }
    };

    const getDispNombre = (dispId) => registrados.find(d => d.id === dispId)?.nombre || dispId;
    const getUserNombre = (userId) => usuarios.find(u => u.id === userId)?.nombre || userId;

    if (loading) return <AlarmasLayout><div className="space-y-4"><Skeleton className="h-40" /><Skeleton className="h-40" /><Skeleton className="h-40" /></div></AlarmasLayout>;

    return (
        <AlarmasLayout>
            <div className="space-y-6 animate-fade-in">
                <h1 className="font-black text-3xl uppercase tracking-tight flex items-center gap-3" data-testid="alarmas-admin-title">
                    <Shield className="h-7 w-7 text-red-500" />
                    Administracion
                </h1>

                {/* ============ DISPOSITIVOS TUYA ============ */}
                <Card className="border-2">
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div>
                                <CardTitle className="flex items-center gap-2"><Wifi className="h-5 w-5" /> Dispositivos Tuya</CardTitle>
                                <CardDescription>Dispositivos detectados en Smart Life</CardDescription>
                            </div>
                            <div className="flex gap-2">
                                <Button variant="outline" size="sm" onClick={async () => {
                                    try {
                                        const res = await fetch(`${API_URL}/api/alarmas/diagnostico`, { headers });
                                        const d = await res.json();
                                        if (d.connection === 'ok') toast.success(`Tuya conectado: ${d.devices_count} dispositivo(s)`);
                                        else toast.error(`Tuya error: ${d.error || 'Sin conexion'}`);
                                    } catch { toast.error('Error'); }
                                }} data-testid="btn-diagnostico">
                                    <Wifi className="h-4 w-4 mr-1" /> Test
                                </Button>
                                <Button variant="outline" size="sm" onClick={loadAll}><RefreshCw className="h-4 w-4" /></Button>
                                <Button size="sm" onClick={() => setDialogAddManual(true)}>
                                    <Plus className="h-4 w-4 mr-1" /> Manual
                                </Button>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {tuyaError && (
                            <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-sm text-amber-700 flex items-center gap-2">
                                <WifiOff className="h-4 w-4" />
                                <span>Tuya: {tuyaError} - Use "Manual" para registrar dispositivos por ID</span>
                            </div>
                        )}
                        {tuyaDevices.length === 0 && !tuyaError ? (
                            <p className="text-muted-foreground text-center py-4">
                                No se detectaron dispositivos. Use el boton "Manual" para agregar por Device ID.
                            </p>
                        ) : (
                            <div className="space-y-2">
                                {tuyaDevices.map(d => {
                                    const yaRegistrado = registrados.some(r => r.tuya_device_id === d.tuya_device_id);
                                    return (
                                        <div key={d.tuya_device_id} className="flex items-center justify-between p-3 border rounded-lg">
                                            <div>
                                                <p className="font-medium">{d.name}</p>
                                                <p className="text-xs text-muted-foreground font-mono">{d.tuya_device_id}</p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Badge variant={d.is_online ? 'default' : 'secondary'} className={d.is_online ? 'bg-emerald-500' : ''}>
                                                    {d.is_online ? 'Online' : 'Offline'}
                                                </Badge>
                                                {yaRegistrado ? (
                                                    <Badge variant="outline">Registrado</Badge>
                                                ) : (
                                                    <Button size="sm" onClick={() => { setDialogAdd({ open: true, device: d }); setFormNombre(d.name); }}>
                                                        <Plus className="h-4 w-4 mr-1" /> Agregar
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* ============ DISPOSITIVOS REGISTRADOS ============ */}
                <Card className="border-2">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2"><Power className="h-5 w-5" /> Dispositivos Registrados ({registrados.length})</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {registrados.length === 0 ? (
                            <p className="text-muted-foreground text-center py-6">Sin dispositivos registrados. Agregue uno arriba.</p>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Nombre</TableHead>
                                        <TableHead>Device ID</TableHead>
                                        <TableHead>Switch</TableHead>
                                        <TableHead className="text-right">Acciones</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {registrados.map(d => (
                                        <TableRow key={d.id}>
                                            <TableCell className="font-bold">{d.nombre}</TableCell>
                                            <TableCell className="font-mono text-xs">{d.tuya_device_id}</TableCell>
                                            <TableCell><Badge variant="outline">{d.codigo_switch}</Badge></TableCell>
                                            <TableCell className="text-right">
                                                <Button variant="ghost" size="icon" onClick={() => handleEliminar(d.id)}>
                                                    <Trash2 className="h-4 w-4 text-destructive" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>

                {/* ============ USUARIOS Y ASIGNACIONES ============ */}
                <Card className="border-2">
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div>
                                <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5" /> Usuarios y Asignaciones</CardTitle>
                                <CardDescription>Cree usuarios y asigne dispositivos que pueden controlar</CardDescription>
                            </div>
                            <Button size="sm" onClick={() => setDialogUser(true)} data-testid="btn-crear-usuario">
                                <UserPlus className="h-4 w-4 mr-1" /> Crear Usuario
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* Formulario rapido de asignacion */}
                        {registrados.length > 0 && usuarios.length > 0 && (
                            <div className="flex flex-col md:flex-row gap-3 p-3 bg-muted/50 rounded-lg">
                                <select id="sel-user" className="flex-1 p-2 border rounded-md bg-background text-sm" data-testid="sel-user">
                                    <option value="">Seleccionar usuario...</option>
                                    {usuarios.map(u => <option key={u.id} value={u.id}>{u.nombre} ({u.email})</option>)}
                                </select>
                                <select id="sel-disp" className="flex-1 p-2 border rounded-md bg-background text-sm" data-testid="sel-disp">
                                    <option value="">Seleccionar dispositivo...</option>
                                    {registrados.map(d => <option key={d.id} value={d.id}>{d.nombre}</option>)}
                                </select>
                                <Button size="sm" onClick={() => {
                                    const uid = document.getElementById('sel-user').value;
                                    const did = document.getElementById('sel-disp').value;
                                    if (uid && did) handleAsignar(uid, did);
                                    else toast.error('Seleccione usuario y dispositivo');
                                }} data-testid="btn-asignar">
                                    <Plus className="h-4 w-4 mr-1" /> Asignar
                                </Button>
                            </div>
                        )}

                        {/* Lista de asignaciones */}
                        {asignaciones.length > 0 ? (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Usuario</TableHead>
                                        <TableHead>Dispositivo</TableHead>
                                        <TableHead>Sonido</TableHead>
                                        <TableHead>Pruebas</TableHead>
                                        <TableHead className="text-right">Acciones</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {asignaciones.map(a => (
                                        <TableRow key={a.id}>
                                            <TableCell>{getUserNombre(a.usuario_id)}</TableCell>
                                            <TableCell className="font-bold">{getDispNombre(a.dispositivo_id)}</TableCell>
                                            <TableCell>
                                                <select
                                                    className="p-1 border rounded text-xs bg-background"
                                                    value={preferencias[a.usuario_id] || 'clasico'}
                                                    onChange={(e) => handleSetSound(a.usuario_id, e.target.value)}
                                                >
                                                    {SOUND_OPTIONS.map(s => (
                                                        <option key={s.value} value={s.value}>{s.label}</option>
                                                    ))}
                                                </select>
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    variant={preferencias[`test_${a.usuario_id}`] ? 'default' : 'outline'}
                                                    size="sm"
                                                    className="h-7 text-xs"
                                                    onClick={() => handleTogglePrueba(a.usuario_id, preferencias[`test_${a.usuario_id}`])}
                                                >
                                                    {preferencias[`test_${a.usuario_id}`] ? 'Si' : 'No'}
                                                </Button>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    <Button variant="ghost" size="icon" onClick={() => {
                                                        const u = usuarios.find(x => x.id === a.usuario_id);
                                                        setDialogEditDireccion({
                                                            open: true,
                                                            userId: a.usuario_id,
                                                            userName: getUserNombre(a.usuario_id),
                                                            nombre: u?.nombre || '',
                                                            direccion: u?.direccion || '',
                                                            email: u?.email || '',
                                                        });
                                                    }} title="Editar usuario" data-testid={`btn-edit-user-${a.usuario_id}`}>
                                                        <UserPlus className="h-4 w-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" onClick={() => { setDialogPwd({ open: true, userId: a.usuario_id, userName: getUserNombre(a.usuario_id) }); setAdminNewPwd(''); }} title="Cambiar contrasena">
                                                        <KeyRound className="h-4 w-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" onClick={() => handleDesasignar(a.id)}>
                                                        <Trash2 className="h-4 w-4 text-destructive" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <p className="text-center text-muted-foreground py-4">Sin asignaciones. Registre dispositivos y cree usuarios primero.</p>
                        )}
                    </CardContent>
                </Card>
                {/* ============ USUARIOS PENDIENTES (del link) ============ */}
                {pendientes.length > 0 && (
                    <Card className="border-2 border-amber-500/40 bg-amber-500/5">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2"><Clock className="h-5 w-5 text-amber-500" /> Solicitudes Pendientes ({pendientes.length})</CardTitle>
                            <CardDescription>Aprueba o rechaza usuarios que se registraron mediante un link público</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Nombre</TableHead>
                                        <TableHead>Dirección</TableHead>
                                        <TableHead>Email</TableHead>
                                        <TableHead>Fecha</TableHead>
                                        <TableHead className="text-right">Acción</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {pendientes.map(p => (
                                        <TableRow key={p.id}>
                                            <TableCell className="font-bold">{p.nombre}</TableCell>
                                            <TableCell>{p.direccion}</TableCell>
                                            <TableCell className="font-mono text-xs">{p.email}</TableCell>
                                            <TableCell className="text-xs text-muted-foreground">
                                                {new Date(p.created_at).toLocaleDateString()}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-2">
                                                    <Button size="sm" onClick={() => handleAprobar(p.id)} data-testid={`btn-aprobar-${p.id}`}>
                                                        <Check className="h-4 w-4 mr-1" /> Aprobar
                                                    </Button>
                                                    <Button size="sm" variant="outline" onClick={() => handleRechazar(p.id)}>
                                                        <XIcon className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                )}

                {/* ============ DIAGNÓSTICO DE USUARIOS ============ */}
                <Card className="border-2">
                    <CardHeader>
                        <div className="flex items-center justify-between flex-wrap gap-2">
                            <div>
                                <CardTitle className="flex items-center gap-2"><Stethoscope className="h-5 w-5 text-emerald-500" /> Estado de Notificaciones por Usuario</CardTitle>
                                <CardDescription>Revisa quién tiene bien configurado su teléfono para recibir alertas</CardDescription>
                            </div>
                            <Button variant="outline" size="sm" onClick={loadAll}>
                                <RefreshCw className="h-4 w-4" />
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {diagnosticos.length === 0 ? (
                            <p className="text-muted-foreground text-center py-4 text-sm">
                                Sin usuarios asignados o sin datos de diagnóstico aún.
                            </p>
                        ) : (
                            <>
                                {/* Resumen */}
                                <div className="grid grid-cols-4 gap-2 mb-4">
                                    {[
                                        { key: 'ok', label: 'OK', color: 'bg-emerald-500' },
                                        { key: 'parcial', label: 'Parcial', color: 'bg-amber-500' },
                                        { key: 'falla', label: 'Falla', color: 'bg-red-500' },
                                        { key: 'sin_diagnostico', label: 'Sin probar', color: 'bg-zinc-500' },
                                    ].map(s => {
                                        const count = diagnosticos.filter(d => d.estado === s.key).length;
                                        return (
                                            <div key={s.key} className={`${s.color} text-white rounded-lg p-3 text-center`}>
                                                <div className="text-2xl font-black">{count}</div>
                                                <div className="text-xs uppercase font-bold opacity-90">{s.label}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Usuario</TableHead>
                                            <TableHead>Dirección</TableHead>
                                            <TableHead>Estado</TableHead>
                                            <TableHead>Detalle</TableHead>
                                            <TableHead className="text-right">Última prueba</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {diagnosticos.map(d => {
                                            const r = d.ultimo_diagnostico?.respuestas || {};
                                            const badgeStyle = {
                                                ok: 'bg-emerald-500',
                                                parcial: 'bg-amber-500',
                                                falla: 'bg-red-500',
                                                sin_diagnostico: 'bg-zinc-500',
                                            }[d.estado];
                                            const badgeLabel = {
                                                ok: 'OK',
                                                parcial: 'Parcial',
                                                falla: 'Falla',
                                                sin_diagnostico: 'Sin probar',
                                            }[d.estado];
                                            return (
                                                <TableRow key={d.usuario_id} data-testid={`diag-row-${d.usuario_id}`}>
                                                    <TableCell className="font-bold">{d.nombre}</TableCell>
                                                    <TableCell className="text-sm">{d.direccion || '-'}</TableCell>
                                                    <TableCell>
                                                        <Badge className={`${badgeStyle} text-white border-0`}>{badgeLabel}</Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        {d.ultimo_diagnostico ? (
                                                            <div className="flex gap-1 text-xs">
                                                                <span className={r.visto ? 'text-emerald-600' : 'text-red-500'} title="Vio la notificación">👁️</span>
                                                                <span className={r.sonido ? 'text-emerald-600' : 'text-red-500'} title="Sonó">🔊</span>
                                                                <span className={r.vibro ? 'text-emerald-600' : 'text-red-500'} title="Vibró">📳</span>
                                                                <span className={r.bloqueo ? 'text-emerald-600' : 'text-red-500'} title="Pantalla bloqueada">🔒</span>
                                                            </div>
                                                        ) : (
                                                            <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-right text-xs text-muted-foreground">
                                                        {d.ultimo_diagnostico
                                                            ? new Date(d.ultimo_diagnostico.created_at).toLocaleString()
                                                            : 'Nunca'}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                                <p className="text-xs text-muted-foreground mt-3">
                                    Leyenda: 👁️ Vio la notificación · 🔊 Sonó · 📳 Vibró · 🔒 Pantalla bloqueada.
                                    Contacta a los usuarios con estado "Falla" o "Sin probar" para que hagan el diagnóstico.
                                </p>
                            </>
                        )}
                    </CardContent>
                </Card>

                {/* ============ LINKS DE REGISTRO PÚBLICO ============ */}
                <Card className="border-2">
                    <CardHeader>
                        <div className="flex items-center justify-between flex-wrap gap-2">
                            <div>
                                <CardTitle className="flex items-center gap-2"><Link2 className="h-5 w-5" /> Links de Registro Público</CardTitle>
                                <CardDescription>Genera un link temporal para que usuarios se registren solos</CardDescription>
                            </div>
                            <div className="flex items-center gap-2">
                                <Label className="text-sm">Valido por (días):</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    max="365"
                                    value={newLinkDays}
                                    onChange={e => setNewLinkDays(Math.max(1, Math.min(365, parseInt(e.target.value) || 7)))}
                                    className="w-20 h-9"
                                    data-testid="input-link-days"
                                />
                                <Button size="sm" onClick={handleCrearLink} data-testid="btn-crear-link">
                                    <Plus className="h-4 w-4 mr-1" /> Crear Link
                                </Button>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {registroLinks.length === 0 ? (
                            <p className="text-muted-foreground text-center py-4">
                                Sin links activos. Crea uno arriba y envíalo a los nuevos usuarios.
                            </p>
                        ) : (
                            <div className="space-y-2">
                                {registroLinks.map(link => {
                                    const url = `${window.location.origin}/api/alarmas/r/${link.token}`;
                                    const expirado = new Date(link.expires_at) < new Date();
                                    return (
                                        <div key={link.id} className={`flex items-center gap-3 p-3 border rounded-lg ${!link.activo || expirado ? 'opacity-50 bg-muted/30' : ''}`}>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <code className="text-xs bg-muted px-2 py-1 rounded truncate max-w-md">{url}</code>
                                                    {!link.activo ? (
                                                        <Badge variant="secondary">Revocado</Badge>
                                                    ) : expirado ? (
                                                        <Badge variant="destructive">Expirado</Badge>
                                                    ) : (
                                                        <Badge className="bg-emerald-500">Activo</Badge>
                                                    )}
                                                </div>
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    Expira: {new Date(link.expires_at).toLocaleString()} · Creado por {link.creado_por_nombre}
                                                </p>
                                            </div>
                                            {link.activo && !expirado && (
                                                <Button size="sm" variant="outline" onClick={() => handleCopyLink(link)} data-testid={`btn-copiar-${link.id}`}>
                                                    {copiedLinkId === link.id ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                                                </Button>
                                            )}
                                            {link.activo && (
                                                <Button size="sm" variant="ghost" onClick={() => handleRevocarLink(link.id)}>
                                                    <Trash2 className="h-4 w-4 text-destructive" />
                                                </Button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* ============ LONG-PRESS CONFIG ============ */}
                <Card className="border-2">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2"><Hand className="h-5 w-5" /> Duración pulsación larga</CardTitle>
                        <CardDescription>Tiempo que el usuario debe mantener presionados los botones antes de activar</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div>
                            <Label className="text-sm font-bold flex items-center gap-2 mb-3"><Power className="h-4 w-4" /> Sirena (encender/apagar)</Label>
                            <div className="flex items-center gap-4">
                                <Slider
                                    value={[longPressMs]}
                                    min={300}
                                    max={3000}
                                    step={100}
                                    onValueChange={v => setLongPressMs(v[0])}
                                    onValueCommit={v => handleSaveLongPress(v[0])}
                                    className="flex-1"
                                    data-testid="slider-longpress"
                                />
                                <span className="font-mono text-lg font-bold w-24 text-right">{longPressMs} ms</span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                                {(longPressMs / 1000).toFixed(1)}s. Recomendado: 600-1500ms.
                            </p>
                        </div>
                        <div>
                            <Label className="text-sm font-bold flex items-center gap-2 mb-3"><ShieldAlert className="h-4 w-4 text-orange-500" /> Botón SOS</Label>
                            <div className="flex items-center gap-4">
                                <Slider
                                    value={[sosLongPressMs]}
                                    min={300}
                                    max={3000}
                                    step={100}
                                    onValueChange={v => setSosLongPressMs(v[0])}
                                    onValueCommit={v => handleSaveSosLongPress(v[0])}
                                    className="flex-1"
                                    data-testid="slider-sos-longpress"
                                />
                                <span className="font-mono text-lg font-bold w-24 text-right">{sosLongPressMs} ms</span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                                {(sosLongPressMs / 1000).toFixed(1)}s. Se suele usar más largo que la sirena para evitar SOS accidentales.
                            </p>
                        </div>
                    </CardContent>
                </Card>

                {/* ============ ICONO DE LA APP ============ */}
                <Card className="border-2">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2"><Shield className="h-5 w-5" /> Icono de la App</CardTitle>
                        <CardDescription>Suba un logo personalizado. Se guarda en la base de datos y sobrevive a los deploys.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex items-center gap-4">
                            <img
                                src={`${API_URL}/api/alarmas/icon/192?t=${Date.now()}`}
                                alt="Icono actual"
                                className="w-16 h-16 rounded-xl border-2 bg-muted object-contain"
                                onError={(e) => { e.currentTarget.src = '/icons/alarmas-192.png'; }}
                                data-testid="img-icon-preview"
                            />
                            <div className="flex-1">
                                <p className="text-sm text-muted-foreground mb-2">Suba una imagen cuadrada (min 512x512px)</p>
                                <input
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp"
                                    className="text-sm"
                                    onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        const fd = new FormData();
                                        fd.append('file', file);
                                        try {
                                            const res = await fetch(`${API_URL}/api/alarmas/admin/upload-icon`, {
                                                method: 'POST',
                                                headers: { 'Authorization': `Bearer ${token}` },
                                                body: fd,
                                            });
                                            const data = await res.json();
                                            if (res.ok) {
                                                toast.success(data.message);
                                                window.location.reload();
                                            }
                                            else toast.error(data.detail || 'Error');
                                        } catch { toast.error('Error al subir'); }
                                    }}
                                    data-testid="upload-icon"
                                />
                            </div>
                        </div>
                        <p className="text-xs text-muted-foreground">El logo se guarda en la base de datos y se utiliza tanto en notificaciones push como en el preview cuando se comparte el link.</p>
                    </CardContent>
                </Card>

                {/* ============ CHAT DE EMERGENCIA ============ */}
                <Card className="border-2">
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div>
                                <CardTitle className="flex items-center gap-2"><MessageCircle className="h-5 w-5" /> Chat de Emergencia</CardTitle>
                                <CardDescription>Chat temporal entre vecinos al activar switch o SOS</CardDescription>
                            </div>
                            <Button
                                variant={chatConfig.activo ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => handleSaveChatConfig({ ...chatConfig, activo: !chatConfig.activo })}
                            >
                                {chatConfig.activo ? 'Activado' : 'Desactivado'}
                            </Button>
                        </div>
                    </CardHeader>
                    {chatConfig.activo && (
                        <CardContent>
                            <div className="flex items-center gap-3">
                                <Label className="text-sm">Duracion (minutos):</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    max="120"
                                    value={chatConfig.duracion_minutos}
                                    onChange={(e) => setChatConfig({ ...chatConfig, duracion_minutos: parseInt(e.target.value) || 15 })}
                                    className="w-24"
                                />
                                <Button size="sm" variant="outline" onClick={() => handleSaveChatConfig(chatConfig)}>
                                    Guardar
                                </Button>
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                                Al activar un switch o SOS, se abrira un chat temporal entre todos los usuarios que comparten dispositivos. Se cierra automaticamente al cumplir el tiempo.
                            </p>
                        </CardContent>
                    )}
                </Card>
            </div>

            {/* Dialog: Agregar desde Tuya */}
            <Dialog open={dialogAdd.open} onOpenChange={(v) => !v && setDialogAdd({ open: false, device: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Registrar Dispositivo</DialogTitle>
                        <DialogDescription>ID: {dialogAdd.device?.tuya_device_id}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Nombre para mostrar</Label>
                            <Input value={formNombre} onChange={e => setFormNombre(e.target.value)} placeholder="Ej: Sirena Principal" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogAdd({ open: false, device: null })}>Cancelar</Button>
                        <Button onClick={handleRegistrar}>Registrar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog: Agregar Manual */}
            <Dialog open={dialogAddManual} onOpenChange={setDialogAddManual}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Registrar Dispositivo Manualmente</DialogTitle>
                        <DialogDescription>Ingrese el Device ID de Tuya (lo encuentra en la app Smart Life o en Tuya IoT Platform)</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Nombre</Label>
                            <Input value={formNombre} onChange={e => setFormNombre(e.target.value)} placeholder="Ej: Sirena Murillo" />
                        </div>
                        <div className="space-y-2">
                            <Label>Tuya Device ID</Label>
                            <Input value={formDeviceId} onChange={e => setFormDeviceId(e.target.value)} placeholder="Ej: eb5469a5989ec1ad55yfq2" className="font-mono" />
                        </div>
                        <div className="space-y-2">
                            <Label>Codigo Switch</Label>
                            <Input value={formSwitch} onChange={e => setFormSwitch(e.target.value)} placeholder="switch_1" className="font-mono" />
                            <p className="text-xs text-muted-foreground">Por defecto: switch_1</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogAddManual(false)}>Cancelar</Button>
                        <Button onClick={handleRegistrarManual}>Registrar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog: Crear Usuario */}
            <Dialog open={dialogUser} onOpenChange={setDialogUser}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Crear Usuario</DialogTitle>
                        <DialogDescription>Este usuario podra controlar los dispositivos que le asigne</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Nombre Completo</Label>
                            <Input value={formUser.nombre} onChange={e => setFormUser({...formUser, nombre: e.target.value})} placeholder="Juan Perez" />
                        </div>
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2"><MapPin className="h-4 w-4" /> Calle y número</Label>
                            <Input value={formUser.direccion} onChange={e => setFormUser({...formUser, direccion: e.target.value})} placeholder="Ej: Murillo 1467" />
                            <p className="text-xs text-muted-foreground">Aparecerá en las notificaciones que este usuario envíe.</p>
                        </div>
                        <div className="space-y-2">
                            <Label>Email</Label>
                            <Input type="email" value={formUser.email} onChange={e => setFormUser({...formUser, email: e.target.value})} placeholder="juan@ejemplo.cl" />
                        </div>
                        <div className="space-y-2">
                            <Label>Contrasena</Label>
                            <Input type="password" value={formUser.password} onChange={e => setFormUser({...formUser, password: e.target.value})} placeholder="Minimo 6 caracteres" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogUser(false)}>Cancelar</Button>
                        <Button onClick={handleCrearUsuario}>Crear Usuario</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog: Editar Usuario (admin) */}
            <Dialog open={dialogEditDireccion.open} onOpenChange={(v) => !v && setDialogEditDireccion({ open: false, userId: '', userName: '', nombre: '', direccion: '', email: '' })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5" /> Editar Usuario</DialogTitle>
                        <DialogDescription>{dialogEditDireccion.userName}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Nombre completo</Label>
                            <Input
                                value={dialogEditDireccion.nombre}
                                onChange={e => setDialogEditDireccion({ ...dialogEditDireccion, nombre: e.target.value })}
                                placeholder="Juan Pérez"
                                data-testid="input-edit-nombre"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2"><MapPin className="h-4 w-4" /> Calle y número</Label>
                            <Input
                                value={dialogEditDireccion.direccion}
                                onChange={e => setDialogEditDireccion({ ...dialogEditDireccion, direccion: e.target.value })}
                                placeholder="Ej: Murillo 1467"
                                data-testid="input-edit-direccion"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Email (usuario para ingresar)</Label>
                            <Input
                                type="email"
                                value={dialogEditDireccion.email}
                                onChange={e => setDialogEditDireccion({ ...dialogEditDireccion, email: e.target.value })}
                                placeholder="usuario@correo.cl"
                                data-testid="input-edit-email"
                            />
                            <p className="text-xs text-muted-foreground">Cambiar el email afecta cómo el usuario inicia sesión.</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogEditDireccion({ open: false, userId: '', userName: '', nombre: '', direccion: '', email: '' })}>Cancelar</Button>
                        <Button onClick={handleSaveDireccionAdmin} data-testid="btn-save-edit-user">Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog: Cambiar Contraseña (admin) */}
            <Dialog open={dialogPwd.open} onOpenChange={(v) => !v && setDialogPwd({ open: false, userId: '', userName: '' })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" /> Cambiar Contrasena</DialogTitle>
                        <DialogDescription>Usuario: {dialogPwd.userName}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Nueva Contrasena</Label>
                            <Input type="password" value={adminNewPwd} onChange={e => setAdminNewPwd(e.target.value)} placeholder="Minimo 6 caracteres" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogPwd({ open: false, userId: '', userName: '' })}>Cancelar</Button>
                        <Button onClick={handleAdminChangePwd}>Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AlarmasLayout>
    );
}
